import { yupResolver } from '@hookform/resolvers/yup'
import { lazyLoad } from '@if/core/utils'
import * as yup from '@if/core/yup'
import {
  ControlledToggleButtonGroup,
  UncontrolledTextFieldDebounced,
  useTranslatedToggleButtonOptionEnum,
} from '@if/mui'
import { Search as SearchIcon } from '@mui/icons-material'
import { InputAdornment, Stack } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import type { ManualDocsAutomationFiltersType } from './ToolBar'

enum ESearchType {
  ClientName = 'clientName',
  Email = 'email',
  Tag = 'tag',
}
const getESearchTypeList = lazyLoad(() => Object.values(ESearchType))

interface SearchSectionProps {
  defaultValues?: ManualDocsAutomationFiltersType | null
  onFiltersChange: (filters: Partial<ManualDocsAutomationFiltersType>) => void
}

const SearchSection = ({ defaultValues, onFiltersChange }: SearchSectionProps) => {

  const resolver = useMemo(
    () =>
      yupResolver(
        yup.object({
          search: yup
            .string()
            .nullable()
            .when('searchType', { is: ESearchType.Email, then: (schema) => schema.email() }),
          searchType: yup.mixed<ESearchType>().oneOf(Object.values(ESearchType)).default(ESearchType.ClientName),
        }),
      ),
    [],
  )

  const formDefaults = useMemo(
    () => ({
      search: defaultValues?.clientNameContains ?? defaultValues?.email ?? defaultValues?.tagSearch ?? null,
      searchType: defaultValues?.email
        ? ESearchType.Email
        : defaultValues?.tagSearch
          ? ESearchType.Tag
          : ESearchType.ClientName,
    }),
    [defaultValues?.clientNameContains, defaultValues?.email, defaultValues?.tagSearch],
  )

  const { subscribe, control } = useForm({ mode: 'onChange', resolver, defaultValues: formDefaults })

  useEffect(() => {
    const cancel = subscribe({
      formState: { isValid: true, values: true },
      callback: ({ values, isValid }) => {
        if (!isValid) return
        const partial: Partial<ManualDocsAutomationFiltersType> = {
          clientNameContains: null,
          email: null,
          tagSearch: null,
        }
        if (values.search) {
          if (values.searchType === ESearchType.Email) partial.email = values.search
          else if (values.searchType === ESearchType.ClientName) partial.clientNameContains = values.search
          else if (values.searchType === ESearchType.Tag) partial.tagSearch = values.search
        }
        onFiltersChange(partial)
      },
    })
    return () => cancel()
  })

  const searchTypeOptions = useTranslatedToggleButtonOptionEnum('enum.searchType', getESearchTypeList())

  return (
    <Stack direction="row" spacing={0} alignItems="center">
      <UncontrolledTextFieldDebounced
        control={control}
        name="search"
        fullWidth={false}
        sx={{
          width: { xs: 180, md: 250 },
          '& .MuiInputBase-root': { height: 32, borderTopRightRadius: 0, borderBottomRightRadius: 0 },
        }}
        size="small"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />
      <ControlledToggleButtonGroup
        control={control}
        name="searchType"
        exclusive
        color="primary"
        size="small"
        options={searchTypeOptions}
        sx={{
          whiteSpace: 'nowrap',
          height: 32,
          '& .MuiToggleButton-root': { borderLeft: 0 },
          '& .MuiToggleButton-root:first-of-type': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
        }}
      />
    </Stack>
  )
}

export default SearchSection
